import { createAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import {
    SEND_MESSAGE_ID,
    STORAGE_KEY_ACCESS_TOKEN,
    STORAGE_KEY_ACTIVE_CHAT,
    STORE_PAYMENT_MODAL_TYPE,
    UNREG_CHATS_ID,
    USER_CHAT_ROLE
} from '../../const';
import chatHistoryApi from '../../service/Api/chatApi';
import chatApi from '../../service/Api/chatApi';
import storageService from '../../service/StorageService';
import { gaEvents } from '../../utils/analytics';
import { updateResendData } from '../../utils/helper';
import { fetchUser, getUserPlan } from './authSlice';
import { setPaymentModalType, tryToCreateOffer } from './modalsSlice';

const initialState = {
    success: true,
    chats: {
        list: [],
        hasMore: true,
        currentPage: 1,
        reloading: false,
        chatDeleteModalConfig: {
            isOpened: false,
            chatId: '',
            chatName: ''
        }
    },
    loading: true,
    error: null,
    activeChat: {
        chatId: null,
        loading: false,
        submitBtnAnimation: false,
        userConnecting: false,
        error: null,
        dateCreated: '',
        name: '',
        messages: [],
        reloading: false,
        hasMore: true,
        currentPage: 1,
        userID: null,
        advisor: null,
        messagesForTypewriter: [],
        typewriterInProgress: false,
        anchorMessageId: '',
        chatHeaderCounter: 0,
        attemptsToChatWithExceededLimit: 0,
        showPaymentIssueNotification: false,
        showUserLimits: true,
        isWithTypewriter: true,
        unreadMessagesTriggerId: null,
        unreadMessages: [],
        wasRemovedByOwner: false,
        chatMembersInfo: {
            loading: true,
            data: null,
            trigger: 0
        },
        lastMessageInChatId: null,
        showReconnectAnimation: false,
        loadedImage: {
            loading: false,
            data: null
        }
    },
    inviteLink: null,
    scrollTrigger: '',
    showMultyChatInvationModal: false,
    updateRemoveChatModal: {
        isOpen: false,
        advisorAlias: null
    }
};

export const fetchChats = createAsyncThunk('chats/fetchChats', async (params, { rejectWithValue }) => {
    try {
        const data = await chatHistoryApi.getChatsHistory(params.page);
        return data.data;
    } catch (error) {
        return rejectWithValue(error);
    }
});

export const startNewChat = createAsyncThunk('chats/testNewChat', async (params, { rejectWithValue }) => {
    try {
        const data = await chatApi.initChat();
        return data.data;
    } catch (error) {
        return rejectWithValue(error?.response?.status);
    }
});

export const startChatWithAdvisor = createAsyncThunk('chats/startChatWithAdvisorById', async (params, { rejectWithValue }) => {
    try {
        const data = await chatApi.initChatWithAdvisor(params);
        return data.data;
    } catch (error) {
        return rejectWithValue(error?.response?.status);
    }
});

export const startChatWithAdvisorByActivities = createAsyncThunk(
    'chats/startChatWithAdvisorByActivities',
    async (params, { rejectedWithValue }) => {
        try {
            const data = await chatApi.initChatWithAdvisorByActivities(params);
            return data.data;
        } catch (error) {
            return rejectedWithValue(error);
        }
    }
);

export const fetchChatMessages = createAsyncThunk('chats/fetchChatMessages', async (params, { rejectedWithValue }) => {
    try {
        const data = await chatApi.getChatMessages(params);
        return data.data;
    } catch (error) {
        return rejectedWithValue(error?.message);
    }
});

export const connectUserToChat = createAsyncThunk('chats/connectUserToChat', async (params, { rejectedWithValue }) => {
    try {
        const data = await chatApi.connectToChat(params);
        return data.data;
    } catch (error) {
        return rejectedWithValue(error?.message);
    }
});

export const sendNewChatMessage = createAsyncThunk('chats/sendNewChatMessage', async (params, { rejectedWithValue }) => {
    try {
        gaEvents.eventMessageSend();
        const normalisedAttachments = params.attachments.map((item) => {
            const newObj = { ...item };
            if (newObj.hasOwnProperty('type')) {
                delete newObj['type'];
            }
            return newObj;
        });
        const formattedParams = { chatId: params.chatId, content: params.content, attachments: normalisedAttachments };
        const data = await chatApi.sendChatMessage(formattedParams);
        gaEvents.eventMessageReceived();
        return data.data;
    } catch (error) {
        return rejectedWithValue(error);
    }
});

export const deleteChat = createAsyncThunk('chats/deleteChat', async (chatId, { rejectedWithValue }) => {
    try {
        const response = await chatApi.deleteChat(chatId);
        return { status: response.data, chatId };
    } catch (error) {
        return rejectedWithValue(error);
    }
});

export const editChatName = createAsyncThunk('chats/editChatName', async ({ chatId, newChatName }, { rejectedWithValue }) => {
    try {
        const response = await chatApi.editChatName(chatId, newChatName);
        return response.data;
    } catch (error) {
        return rejectedWithValue(error);
    }
});

export const getChatMembersInfo = createAsyncThunk('chats/getChatMembersInfo', async (chatId, { rejectedWithValue }) => {
    try {
        const response = await chatApi.getChatInfo(chatId);
        return response.data;
    } catch (error) {
        return rejectedWithValue(error);
    }
});

export const loadImageFile = createAsyncThunk('chats/loadImageFile', async (params, { rejectWithValue }) => {
    try {
        const response = await chatApi.loadImage(params);
        return response.data;
    } catch (error) {
        console.log('error', error);
        return rejectWithValue(error.message);
    }
});

export const showPaymentModalWithOfferActivation = createAsyncThunk(
    'chats/showPaymentModalWithOfferActivation',
    async (params, { rejectWithValue, getState, dispatch }) => {
        try {
            const state = getState();
            dispatch(tryToCreateOffer());

            const userPlan = getUserPlan(state);
            dispatch(setPaymentModalType(!userPlan.isTrial ? STORE_PAYMENT_MODAL_TYPE.trial : STORE_PAYMENT_MODAL_TYPE.subscription));
        } catch (error) {
            console.log('error', error);
            return rejectWithValue(error.message);
        }
    }
);

export const messageAddedInChat = createAction('chat/messageAddedInChat');
export const chatWasRemovedByOwner = createAction('chat/chatWasRemovedByOwner');
export const resetUnreadMessagesTriggerId = createAction('chat/resetUnreadMessagesTriggerId');
export const setMultyChatInvationModalVisibility = createAction('chat/setMultyChatInvationModalVisibility');
export const setUpdateRemoveChatModal = createAction('chat/setUpdateRemoveChatModal');
export const resetImageFile = createAction('chat/resetImageFile');
export const attemptToChatWithExceededLimit = createAction('chat/attemptToChatWithExceededLimit');

export const getChatsState = (store) => store.chats.chats;
export const getActiveChatState = (store) => store.chats.activeChat;
export const getLastMessageInChatId = (store) => store.chats.activeChat.lastMessageInChatId;
export const getActiveChatLoadingState = (store) => store.chats.activeChat.loading;
export const getActiveChatBtnAnimationState = (store) => store.chats.activeChat.submitBtnAnimation;
export const getActiveTypewriterState = (store) => store.chats.activeChat.typewriterInProgress;
export const getChatId = (store) => store.chats.activeChat.chatId;
export const getUserChatId = (store) => store.chats.activeChat.userID;
export const getAnswerIntoViewScrollTrigger = (store) => store.chats.scrollTrigger;
export const getAdvisorInfo = (store) => store.chats.activeChat.advisor;
export const getChatHeaderCounter = (store) => store.chats.activeChat.chatHeaderCounter;
export const getPaymentIssueNotificationState = (store) => store.chats.activeChat.showPaymentIssueNotification;
export const getUserLimitsState = (store) => store.chats.activeChat.showUserLimits;
export const getIsWithTypewriter = (store) => store.chats.activeChat.isWithTypewriter;
export const getUnreadMessagesTriggerId = (store) => store.chats.activeChat.unreadMessagesTriggerId;
export const getWssRemovedChatTrigger = (store) => store.chats.activeChat.wasRemovedByOwner;
export const getChatMembersData = (store) => store.chats.activeChat.chatMembersInfo;
export const getChatDeleteModalConfig = (store) => store.chats.chats.chatDeleteModalConfig;
export const getMultiChatModalVisibility = (store) => store.chats.showMultyChatInvationModal;
export const getShorChatInfo = (store) => store.chats.activeChat.chatMembersInfo.data;
export const getParticipantTriggerRequest = (store) => store.chats.activeChat.chatMembersInfo.trigger;
export const getNewUnreadMessages = (store) => store.chats.activeChat.unreadMessages;
export const getShowReconnectAnimation = (store) => store.chats.activeChat.showReconnectAnimation;
export const getUserConnectingState = (store) => store.chats.activeChat.userConnecting;
export const getChatLoadedImage = (store) => store.chats.activeChat.loadedImage;
export const getUpdateRemoveChatModalState = (store) => store.chats.updateRemoveChatModal;
export const getAttemptsToChatWithExceededLimit = (store) => store.chats.activeChat.attemptsToChatWithExceededLimit;

const chatsSlice = createSlice({
    name: 'chats',
    initialState,
    reducers: {
        clearChatsError(store) {
            store.error = null;
        },
        triggerAnswerIntoViewScroll(store) {
            store.scrollTrigger = new Date().getTime();
        },
        clearActiveChatError(store) {
            store.activeChat.error = null;
        },
        setTypewriterProgressState(store, { payload }) {
            store.activeChat.typewriterInProgress = payload;
        },
        cleanMessagesForTypewriter(store) {
            store.activeChat.messagesForTypewriter = [];
        },
        updateMessagesForTypewriter(store) {
            store.activeChat.messagesForTypewriter = store.activeChat.messagesForTypewriter.splice(1);
        },
        appendNewMessageToChat(store, { payload }) {
            store.activeChat.messages = [payload, ...store.activeChat.messages];
        },
        setChatHeaderCounter(store, { payload }) {
            store.activeChat.chatHeaderCounter = payload;
        },
        togglePaymentIssueNotification(store, { payload }) {
            store.activeChat.showPaymentIssueNotification = payload;
        },
        setChatDeleteModalConfig(store, { payload }) {
            store.chats.chatDeleteModalConfig = {
                ...store.chats.chatDeleteModalConfig,
                ...payload
            };
        },
        setActiveChatId(store, { payload }) {
            store.activeChat.chatId = payload;
        },
        setLastMessageInChatId(store, { payload }) {
            store.activeChat.lastMessageInChatId = payload;
        },
        setShowReconnectAnimation(store, { payload }) {
            store.activeChat.showReconnectAnimation = payload;
        }
    },
    extraReducers(builder) {
        builder
            .addCase(fetchChats.pending, (store, { meta: { arg } }) => {
                const reloading = typeof arg?.reloading === 'undefined' ? true : arg.reloading;
                store.chats = {
                    ...store.chats,
                    loading: true,
                    error: null,
                    reloading
                };
            })
            .addCase(fetchChats.fulfilled, (store, { payload }) => {
                const reloading = store.chats.reloading;
                store.chats = {
                    ...store.chats,
                    loading: false,
                    reloading: false,
                    list: reloading ? [...payload.chats] : [...store.chats.list, ...payload.chats],
                    success: payload.success,
                    hasMore: payload?.currentPage < payload?.allPages,
                    currentPage: Number.parseInt(payload?.currentPage, 10) + 1
                };
            })
            .addCase(fetchChats.rejected, (store, { payload }) => {
                store.chats = {
                    ...store.chats,
                    loading: false,
                    error: payload?.error,
                    success: payload?.success,
                    reloading: false
                };
            })
            .addCase(startNewChat.pending, (store) => {
                store.activeChat = {
                    ...store.activeChat,
                    loading: true,
                    error: null
                };
            })
            .addCase(startNewChat.fulfilled, (store, { payload }) => {
                gaEvents.eventStartChat(payload?.advisor?.id);
                const unregristerCatIds = storageService.get(UNREG_CHATS_ID) || [];
                const userToken = storageService.get(STORAGE_KEY_ACCESS_TOKEN);
                store.activeChat = {
                    ...store.activeChat,
                    loading: false,
                    dateCreated: payload?.chat?.dateCreated,
                    name: payload?.chat?.name,
                    messages: payload?.messages,
                    advisor: payload?.advisor
                };
                storageService.add(STORAGE_KEY_ACTIVE_CHAT, payload?.id);
                if (unregristerCatIds.length > 19) {
                    unregristerCatIds.shift();
                }
                if (!userToken) {
                    storageService.add(UNREG_CHATS_ID, [...unregristerCatIds, payload?.id]);
                }
            })
            .addCase(startNewChat.rejected, (store, { payload }) => {
                store.activeChat = {
                    ...store.activeChat,
                    loading: false,
                    error: payload
                };
            })
            .addCase(startChatWithAdvisor.pending, (store) => {
                store.activeChat = {
                    ...store.activeChat,
                    loading: true,
                    error: null
                };
            })
            .addCase(startChatWithAdvisor.fulfilled, (store, { payload }) => {
                gaEvents.eventStartChat(payload?.advisor?.id);
                const unregristerCatIds = storageService.get(UNREG_CHATS_ID) || [];
                const userToken = storageService.get(STORAGE_KEY_ACCESS_TOKEN);
                store.activeChat = {
                    ...store.activeChat,
                    loading: false,
                    dateCreated: payload?.chat?.dateCreated,
                    name: payload?.chat?.name,
                    messages: payload?.messages,
                    advisor: payload?.advisor
                };
                storageService.add(STORAGE_KEY_ACTIVE_CHAT, payload?.id);
                if (unregristerCatIds.length > 19) {
                    unregristerCatIds.shift();
                }
                if (!userToken) {
                    storageService.add(UNREG_CHATS_ID, [...unregristerCatIds, payload?.id]);
                }
            })
            .addCase(startChatWithAdvisor.rejected, (store, { payload }) => {
                store.activeChat = {
                    ...store.activeChat,
                    loading: false,
                    error: payload
                };
            })
            .addCase(startChatWithAdvisorByActivities.pending, (store) => {
                store.activeChat = {
                    ...store.activeChat,
                    loading: true,
                    error: null
                };
            })
            .addCase(startChatWithAdvisorByActivities.fulfilled, (store, { payload }) => {
                gaEvents.eventStartChat(payload?.advisor?.id);
                const unregristerCatIds = storageService.get(UNREG_CHATS_ID) || [];
                const userToken = storageService.get(STORAGE_KEY_ACCESS_TOKEN);
                store.activeChat = {
                    ...store.activeChat,
                    loading: false,
                    dateCreated: payload?.chat?.dateCreated,
                    name: payload?.chat?.name,
                    messages: payload?.messages,
                    advisor: payload?.advisor
                };
                storageService.add(STORAGE_KEY_ACTIVE_CHAT, payload?.id);
                if (unregristerCatIds.length > 19) {
                    unregristerCatIds.shift();
                }
                if (!userToken) {
                    storageService.add(UNREG_CHATS_ID, [...unregristerCatIds, payload?.id]);
                }
            })
            .addCase(startChatWithAdvisorByActivities.rejected, (store, { payload }) => {
                store.activeChat = {
                    ...store.activeChat,
                    loading: false,
                    error: payload
                };
            })
            .addCase(fetchChatMessages.pending, (store, { meta: { arg } }) => {
                const reloading = typeof arg?.reloading === 'undefined' ? true : arg.reloading;
                store.activeChat = {
                    ...store.activeChat,
                    loading: true,
                    reloading: reloading,
                    error: null,
                    isWithTypewriter: true,
                    ...(reloading && { advisor: null })
                };
            })
            .addCase(fetchChatMessages.fulfilled, (store, { payload }) => {
                const reloading = store.activeChat.reloading;
                const advisor = payload.messages.find((message) => message.role === USER_CHAT_ROLE.ASSISTANT);
                const getFilteredMessages = () => {
                    const newList = payload.messages.filter((newobj) => {
                        return store.activeChat.messages.filter((oldObj) => oldObj.id === newobj.id).length === 0;
                    });
                    return [...store.activeChat.messages, ...newList.reverse()];
                };

                store.activeChat = {
                    ...store.activeChat,
                    loading: false,
                    reloading: false,
                    name: payload?.name,
                    hasMore: payload?.currentPage < payload?.allPages,
                    currentPage: Number.parseInt(payload?.currentPage, 10) + 1,
                    messages: reloading ? [...payload.messages.reverse()] : getFilteredMessages(),
                    userID: payload?.userID,
                    advisor: advisor?.advisor || null,
                    anchorMessageId: payload.messages[payload.messages.length - 1]?.id
                };
            })
            .addCase(fetchChatMessages.rejected, (store, payload) => {
                store.activeChat = {
                    ...store.activeChat,
                    loading: false,
                    reloading: false,
                    error: payload
                };
            })
            .addCase(sendNewChatMessage.pending, (store, { meta: { arg } }) => {
                updateResendData(storageService.get(STORAGE_KEY_ACTIVE_CHAT), false, {
                    content: arg.content,
                    attachments: arg.attachments
                });
                store.activeChat = {
                    ...store.activeChat,
                    loading: true,
                    submitBtnAnimation: true,
                    typewriterInProgress: true,
                    error: null,
                    isWithTypewriter: true,
                    messages: [
                        {
                            content: arg.content,
                            attachments: arg.attachments,
                            role: USER_CHAT_ROLE.USER,
                            id: SEND_MESSAGE_ID
                        },
                        ...store.activeChat.messages
                    ]
                };
            })
            .addCase(sendNewChatMessage.fulfilled, (store, { payload }) => {
                updateResendData(storageService.get(STORAGE_KEY_ACTIVE_CHAT), false, '', true);
                const replyTo = payload.answer.find((item) => item.role === USER_CHAT_ROLE.USER);
                const filteredAnswers = payload.answer.filter((item) => item.role !== USER_CHAT_ROLE.USER);
                const plan = payload?.plan;
                const isPaymentIssue = plan ? plan?.isFree && plan?.isTrial && !plan?.isCancel : false;
                const isUserLimits = plan ? plan?.isFree : true;
                const formattedMessages = store.activeChat.messages.map((item) => {
                    if (item.id === SEND_MESSAGE_ID) {
                        item.id = replyTo.id;
                    }
                    return item;
                });
                store.activeChat = {
                    ...store.activeChat,
                    messages: formattedMessages,
                    loading: false,
                    submitBtnAnimation: false,
                    messagesForTypewriter: [...filteredAnswers],
                    showPaymentIssueNotification: isPaymentIssue,
                    showUserLimits: isUserLimits
                };
            })
            .addCase(sendNewChatMessage.rejected, (store, { payload, meta: { arg } }) => {
                updateResendData(storageService.get(STORAGE_KEY_ACTIVE_CHAT), true, {
                    content: arg.content,
                    attachments: arg.attachments
                });
                store.activeChat = {
                    ...store.activeChat,
                    loading: false,
                    submitBtnAnimation: false,
                    typewriterInProgress: false,
                    error: payload,
                    messages: [...store.activeChat.messages.slice(1)]
                };
            })
            .addCase(deleteChat.fulfilled, (store, { payload }) => {
                if (payload.status.success !== true) {
                    return;
                }

                store.chats.list = store.chats.list.filter((chat) => {
                    if (chat.id === payload.chatId) {
                        return;
                    }
                    return chat;
                });
            })
            .addCase(editChatName.fulfilled, (store, { payload }) => {
                store.chats.list = store.chats.list.map((chat) => {
                    if (chat.id === payload.chat.id) {
                        chat.name = payload.chat.name;
                    }
                    return chat;
                });
            })
            .addCase(fetchUser.fulfilled, (store, { payload }) => {
                store.activeChat = {
                    ...store.activeChat,
                    showUserLimits: payload?.plan?.isFree
                };
            })
            .addCase(messageAddedInChat, (store, { payload }) => {
                const isArray = Array.isArray(payload);
                const messagesToArray = isArray ? payload : [payload];
                const triggerMembersCountRoles = [USER_CHAT_ROLE.USER_CHAT_DISCONNECTED, USER_CHAT_ROLE.USER_CHAT_CONNECTED];
                const objIds = store.activeChat.messages.map((item) => item.id || item._id);

                const applyMemberTrigger = triggerMembersCountRoles.indexOf(messagesToArray[0].role) > -1;

                const filteredArray = messagesToArray.filter((item) => !objIds.includes(item.id || item._id));

                if (!store.activeChat.unreadMessagesTriggerId) {
                    store.activeChat.unreadMessagesTriggerId = filteredArray[0].id || filteredArray[0]._id;
                }

                if (filteredArray.length > 0) {
                    store.activeChat.unreadMessages = [...filteredArray, ...store.activeChat.unreadMessages];
                }

                store.activeChat.isWithTypewriter = false;
                store.activeChat.messages = [...filteredArray.reverse(), ...store.activeChat.messages];

                if (applyMemberTrigger) {
                    store.activeChat.chatMembersInfo.trigger = new Date().getMilliseconds();
                }
            })
            .addCase(resetUnreadMessagesTriggerId, (store) => {
                store.activeChat.unreadMessagesTriggerId = null;
                store.activeChat.unreadMessages = [];
            })
            .addCase(chatWasRemovedByOwner, (store, { payload }) => {
                store.activeChat.wasRemovedByOwner = payload;
            })
            .addCase(getChatMembersInfo.pending, (store) => {
                store.activeChat.chatMembersInfo.loading = true;
            })
            .addCase(getChatMembersInfo.fulfilled, (store, { payload }) => {
                store.activeChat.chatMembersInfo.loading = false;
                store.activeChat.chatMembersInfo.data = payload.data;
            })
            .addCase(getChatMembersInfo.rejected, (store) => {
                store.activeChat.chatMembersInfo.loading = false;
            })
            .addCase(setMultyChatInvationModalVisibility, (store, { payload }) => {
                store.showMultyChatInvationModal = payload;
            })
            .addCase(connectUserToChat.pending, (store) => {
                store.activeChat.userConnecting = true;
            })
            .addCase(connectUserToChat.fulfilled, (store) => {
                store.activeChat.userConnecting = false;
            })
            .addCase(connectUserToChat.rejected, (store) => {
                store.activeChat.userConnecting = false;
            })
            .addCase(loadImageFile.pending, (store) => {
                store.activeChat.loadedImage.loading = true;
            })
            .addCase(loadImageFile.fulfilled, (store, { payload }) => {
                store.activeChat.loadedImage.loading = false;
                store.activeChat.loadedImage.data = payload;
            })
            .addCase(loadImageFile.rejected, (store) => {
                store.activeChat.loadedImage.loading = false;
            })
            .addCase(resetImageFile, (store) => {
                store.activeChat.loadedImage = {
                    loading: false,
                    data: null
                };
            })
            .addCase(setUpdateRemoveChatModal, (store, { payload }) => {
                store.updateRemoveChatModal = { isOpen: payload.isOpen, alias: payload.alias };
            })
            .addCase(attemptToChatWithExceededLimit, (store) => {
                store.activeChat.attemptsToChatWithExceededLimit += 1;
            });
    }
});

export const chatsReducer = chatsSlice.reducer;
export const {
    clearChatsError,
    triggerAnswerIntoViewScroll,
    clearActiveChatError,
    cleanMessagesForTypewriter,
    updateMessagesForTypewriter,
    appendNewMessageToChat,
    setTypewriterProgressState,
    setChatHeaderCounter,
    togglePaymentIssueNotification,
    setChatDeleteModalConfig,
    setActiveChatId,
    setLastMessageInChatId,
    setShowReconnectAnimation
} = chatsSlice.actions;
